import React, { useState } from 'react';

const experienceArray = [
    {
        expId: 'gp',
        tabName: 'Telus Health',
        tech: ['React', 'TypeScript', 'OAuth2/OIDC', 'Datadog', 'Jest/RTL'],
        dateRange: 'May 2022 - Present',
        description: [
            'Led the frontend development of a complex authentication migration, designing a custom OAuth2/OIDC client that replaced AWS Amplify across web and mobile apps',
            'Reduced deployment time from 4 hours to 15 minutes by building a centralised deployment system across multiple repositories',
            'Built a shared frontend library adopted across 4+ repositories, reducing code duplication across the engineering organisation',
            'Established frontend observability by integrating Datadog monitoring, source maps, and RUM - enabling diagnosis of previously untraceable production issues',
            'Contributed to Microsoft\'s open-source Omnichannel Chat Widget, fixing a backwards-compatibility bug',
            'Drove engineering productivity improvements including AI tooling integrations and workflow automation',
        ],
    },
    {
        expId: 'cog',
        tabName: 'Cognito.IQ',
        tech: ['React', 'Redux', 'Recharts', 'Go', 'AWS Lambda', 'MySQL'],
        dateRange: 'May 2021 - Mar 2022',
        description: [
            'A SaaS solution to develop domain-agnostic, configurable data analytics dashboards for several large UK and international B2B customers in retail logistics and field service',
            'Worked on an in-house configurable dashboard builder to visualise business data',
            'Built AWS Lambda functions with dynamic MySQL queries for flexible, domain-agnostic data visualisation',
        ],
    },
    {
        expId: 'jmt',
        tabName: 'JMT / Freelance',
        tech: ['React Native', 'Redux', 'IBM Cloud', 'Watson Assistant', 'Node-RED'],
        dateRange: 'Aug 2020 - May 2021',
        description: [
            'Developed a prototype smart home tablet app for newly built homes, enabling control of IoT devices and assistant chat for landlord enquiries',
            'Connected smart home devices (light switches, thermometers, CO2) to a MobiusFlow Edge Gateway',
            'Built a real-time chat system connecting to Watson Assistant through IBM Cloud using WebSockets',
            'Freelanced on various web projects during this period',
        ],
    },
    {
        expId: 'cl',
        tabName: 'Compare Locals',
        tech: ['React', 'Redux', 'Saga', 'Material UI', 'MySQL', 'PHP/Laravel'],
        dateRange: 'Aug 2019 - Jun 2020',
        description: [
            'Worked on a marketplace website to compare and book tradespeople',
            'Included functionality for admins, service providers and public users',
            'Autonomously developed the front-end as part of a small team',
            'Contributed to BREAD API endpoints using PHP/Laravel and Eloquent',
        ],
    },
    {
        expId: 'shift',
        tabName: 'Shift',
        tech: ['React', 'React Native', 'GCP', 'MongoDB', 'Parse'],
        dateRange: 'Feb 2019 - Aug 2019',
        description: [
            'Worked on an online removal marketplace website and a Cordova mobile app for service providers (imagine Uber but for removals)',
            'Created a React Native tablet app used in self-storage facilities for booking removals',
            'Gained exposure to Linux servers and GCP through manual server management',
        ],
    },
    {
        expId: 'vertical',
        tabName: 'Vertical+',
        tech: ['PHP', 'MySQL', 'HTML', 'CSS', 'JavaScript'],
        dateRange: 'Sep 2017 - Sep 2018',
        description: [
            'One-year work placement during university as a PHP developer on an e-commerce platform used by over 50 clients in the UK',
            'Worked on core parts of the platform and replaced WordPress with in-house blog functionality',
            'Learnt essential knowledge of web technologies using LAMP stack',
        ],
    },
];

const Experience = () => {
    const [activeTabKey, setActiveTabKey] = useState('gp');
    const activeExp = experienceArray.find(exp => exp.expId === activeTabKey);

    return (
        <div className="flex flex-col md:flex-row mt-6 gap-0">
            {/* Sidebar tabs */}
            <div
                role="tablist"
                aria-label="Work experience"
                className="flex flex-row md:flex-col md:min-w-[180px] md:border-r border-b md:border-b-0 border-border overflow-x-auto md:overflow-x-visible"
            >
                {experienceArray.map(({ expId, tabName }) => {
                    const isActive = expId === activeTabKey;
                    return (
                        <button
                            key={expId}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={`panel-${expId}`}
                            id={`tab-${expId}`}
                            onClick={() => setActiveTabKey(expId)}
                            className={`text-left px-4 py-3 text-sm whitespace-nowrap transition-all duration-theme ease-theme border-b-2 md:border-b-0 md:border-l-2 hover:bg-tab-hover hover:text-primary-main ${
                                isActive
                                    ? 'border-primary-main text-primary-main font-semibold bg-tab-hover'
                                    : 'border-transparent text-primary-dark'
                            }`}
                        >
                            {tabName}
                        </button>
                    );
                })}
            </div>

            {/* Content panel */}
            <div
                role="tabpanel"
                id={`panel-${activeExp.expId}`}
                aria-labelledby={`tab-${activeExp.expId}`}
                className="flex-1 md:pl-8 pt-6 md:pt-0 min-h-[300px]"
            >
                <h3 className="text-base font-semibold text-txt-contrast m-0">
                    {activeExp.tabName}
                </h3>
                <p className="text-sm text-primary-dark mt-1 mb-4">{activeExp.dateRange}</p>

                <ul className="ul-arrows mb-6">
                    {activeExp.description.map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>

                <div className="flex flex-wrap gap-2">
                    {activeExp.tech.map((item, i) => (
                        <span
                            key={i}
                            className="px-3 py-1 rounded-full text-xs bg-bg-body3 text-txt-contrast border border-border"
                        >
                            {item}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Experience;
